import { Head } from "@inertiajs/react"
import { Box, Flex, Heading, Text } from "@radix-ui/themes"
import { z } from "zod"

import CopyableCode from "@/components/copyable-code"

import ProjectLayout, { type BreadcrumbItem } from "@/layouts/project-layout"
import { projectDashboardPath } from "@/routes"
import { ProjectSchema } from "@/schemas"

const PropsSchema = z.object({
  project: ProjectSchema,
  current_domain: z.string(),
  current_scheme: z.string(),
})

type Props = z.infer<typeof PropsSchema>

export default function McpDocs(props: Props) {
  const { project, current_domain, current_scheme } = PropsSchema.parse(props)
  const serverUrl = `${current_scheme}://${current_domain}/mcp`
  const configuration = `{
  "mcpServers": {
    "spinus": {
      "type": "http",
      "url": "${serverUrl}",
      "headers": {
        "Authorization": "Bearer ${project.api_key}"
      }
    }
  }
}`

  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: project.name,
      href: projectDashboardPath(project.slug),
    },
    {
      title: "MCP",
    },
  ]

  return (
    <ProjectLayout projectId={project.slug} breadcrumbs={breadcrumbs}>
      <Head title={`${project.name} - MCP`} />
      <Flex direction="column" gap="6" p="6" height="100%">
        <Box>
          <Heading size="6" weight="medium" mb="2">
            Use Spinus with AI agents
          </Heading>
          <Text size="2" color="gray">
            Connect an AI agent to manage this project&apos;s custom lists and email templates
            through MCP.
          </Text>
        </Box>

        <Heading size="5" weight="medium">
          Connect your agent
        </Heading>

        <Flex direction="column" gap="3">
          <Text size="2">1. Open the MCP server settings in your AI agent or coding tool.</Text>
          <Text size="2">
            2. Add a Streamable HTTP server named <Text weight="medium">Spinus</Text> with the
            following URL and authorisation header.
          </Text>
        </Flex>

        <Text size="2" weight="medium">
          Server URL
        </Text>
        <CopyableCode code={serverUrl} />

        <Text size="2" weight="medium">
          Authorisation header
        </Text>
        <CopyableCode code={`Bearer ${project.api_key}`} />

        <Text size="2" weight="medium">
          Example configuration
        </Text>
        <CopyableCode code={configuration} className="whitespace-pre-wrap" />
        <Text size="1" color="gray">
          Configuration fields differ between agents. Use the same URL and pass the value above as
          the Authorization header if your agent uses another format.
        </Text>

        <Heading size="5" weight="medium">
          Available tools
        </Heading>

        <Flex direction="column" gap="2">
          <Text size="2">
            • <Text weight="medium">custom_list_create</Text> creates a custom list from existing
            customer email addresses.
          </Text>
          <Text size="2">
            • <Text weight="medium">email_templates</Text> lists templates or finds one by its
            custom ID.
          </Text>
          <Text size="2">
            • <Text weight="medium">email_template_create</Text> creates a template with a subject,
            Markdown body and visual style.
          </Text>
          <Text size="2">
            • <Text weight="medium">email_template_update</Text> edits or renames a template.
          </Text>
          <Text size="2">
            • <Text weight="medium">email_template_delete</Text> permanently deletes a template.
          </Text>
        </Flex>

        <Heading size="5" weight="medium">
          Try it
        </Heading>
        <Text size="2" color="gray">
          Once connected, ask your agent to use Spinus explicitly:
        </Text>
        <CopyableCode
          code="Use the Spinus MCP server to create a custom list from the email addresses in this CSV file. Reformat the file as needed before uploading the list."
          className="whitespace-pre-wrap"
        />

        <Text size="1" color="gray">
          Your API key grants access to this project. Keep it private and do not share the
          configuration publicly.
        </Text>
      </Flex>
    </ProjectLayout>
  )
}
